/**
 * Password reset and change (ADR 0026).
 * - Forgot: Supabase mails a link that opens the web app (the desktop app
 *   cannot receive links), which then asks for a new password.
 * - Change: the current password is checked first, then the new one is set.
 */
import { create } from 'zustand';
import { linkTarget } from './config';
import { supabase } from './useSync';

export const MIN_PASSWORD = 8;

const hash = typeof window !== 'undefined' ? window.location.hash : '';

/** Opened from a reset or invite mail: Supabase puts `type=…` in the address (read before the client clears it). */
const openedFromReset = /(^|[#&?])type=(recovery|invite)(&|$)/.test(hash);

/** Opened from an invite mail: the dialog welcomes the colleague instead of speaking of a "new" password. */
export const openedFromInvite = /(^|[#&?])type=invite(&|$)/.test(hash);

/**
 * The mail link no longer worked (expired, or already opened, e.g. by a mail
 * scanner that follows links): Supabase sends the user back with an error.
 */
export const linkExpired = /(^|[#&?])error_code=otp_expired(&|$)/.test(hash);

/**
 * A mail link that only carries its code (`?token_hash=…&type=invite|recovery`,
 * see apps/web/public/mail). The code is used when the password is saved, not
 * when the page opens: mail scanners that open every link (Microsoft Safe
 * Links and the like) would otherwise use it up before the colleague clicks.
 */
export interface MailLink {
  tokenHash: string;
  type: 'invite' | 'recovery';
}

function readMailLink(): MailLink | null {
  if (typeof window === 'undefined') return null;
  const q = new URLSearchParams(window.location.search);
  const tokenHash = q.get('token_hash');
  const type = q.get('type');
  return tokenHash && (type === 'invite' || type === 'recovery') ? { tokenHash, type } : null;
}

export const mailLink = readMailLink();

/** Forget the code in the address, so a reload or a shared address doesn't reuse it. */
export function clearMailLink(): void {
  history.replaceState(null, '', window.location.pathname);
}

/** "Set a new password" is asked once the reset link has signed the user in. */
export const usePasswordRecovery = create<{ pending: boolean; done: () => void }>((set) => ({
  pending: openedFromReset,
  done: () => set({ pending: false }),
}));

export type PasswordError =
  'wrong-password' | 'too-short' | 'mismatch' | 'rate-limit' | 'expired' | 'failed';

export class PasswordProblem extends Error {
  constructor(readonly code: PasswordError) {
    super(code);
  }
}

const reason = (e: { status?: number; message?: string } | null): PasswordError =>
  e?.status === 429 || /rate|too many/i.test(e?.message ?? '') ? 'rate-limit' : 'failed';

/** Mail a reset link. Says nothing about whether the address has an account. */
export async function requestPasswordReset(email: string): Promise<void> {
  const { error } = await supabase().auth.resetPasswordForEmail(email.trim(), {
    ...(linkTarget() ? { redirectTo: linkTarget() } : {}),
  });
  if (error && reason(error) === 'rate-limit') throw new PasswordProblem('rate-limit');
}

function checkNew(next: string, repeat: string) {
  if (next.length < MIN_PASSWORD) throw new PasswordProblem('too-short');
  if (next !== repeat) throw new PasswordProblem('mismatch');
}

/** After a reset link: set the new password for the signed-in user. */
export async function setNewPassword(next: string, repeat: string): Promise<void> {
  checkNew(next, repeat);
  const { error } = await supabase().auth.updateUser({ password: next });
  if (error) throw new PasswordProblem(reason(error));
}

/** From a mail link: use its code (this signs in), then set the password. */
export async function setPasswordFromMail(
  link: MailLink,
  next: string,
  repeat: string,
): Promise<void> {
  checkNew(next, repeat);
  const sb = supabase();
  const { error: verify } = await sb.auth.verifyOtp({
    token_hash: link.tokenHash,
    type: link.type,
  });
  if (verify) throw new PasswordProblem(reason(verify) === 'rate-limit' ? 'rate-limit' : 'expired');
  const { error } = await sb.auth.updateUser({ password: next });
  if (error) throw new PasswordProblem(reason(error));
}

/** Signed in: change the password, after checking the current one. */
export async function changePassword(current: string, next: string, repeat: string): Promise<void> {
  checkNew(next, repeat);
  const sb = supabase();
  const email = (await sb.auth.getSession()).data.session?.user.email;
  if (!email) throw new PasswordProblem('failed');
  const check = await sb.auth.signInWithPassword({ email, password: current });
  if (check.error)
    throw new PasswordProblem(check.error.status === 429 ? 'rate-limit' : 'wrong-password');
  const { error } = await sb.auth.updateUser({ password: next });
  if (error) throw new PasswordProblem(reason(error));
}
